import { useState } from 'react'
import {
  ChevronRightIcon,
  FolderIcon,
  DocumentIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
} from '@heroicons/react/24/outline'
import { SkillTreeNode } from '../../services/skillApi'

interface SkillTreeProps {
  nodes: SkillTreeNode[]
  selectedId?: number | null
  onSelectFile: (node: SkillTreeNode) => void
  onSelectFolder?: (node: SkillTreeNode) => void
  onDownloadFolder?: (node: SkillTreeNode) => void
  onUploadIntoFolder?: (node: SkillTreeNode) => void
}

const SkillTreeItem = ({
  node,
  depth,
  selectedId,
  onSelectFile,
  onSelectFolder,
  onDownloadFolder,
  onUploadIntoFolder,
}: {
  node: SkillTreeNode
  depth: number
  selectedId?: number | null
  onSelectFile: (node: SkillTreeNode) => void
  onSelectFolder?: (node: SkillTreeNode) => void
  onDownloadFolder?: (node: SkillTreeNode) => void
  onUploadIntoFolder?: (node: SkillTreeNode) => void
}) => {
  const [open, setOpen] = useState(depth < 2)
  const selected = selectedId === node.id

  if (node.nodeType === 'FOLDER') {
    return (
      <div>
        <div
          className={`group flex items-center gap-1 rounded-md px-2 py-1.5 text-sm hover:bg-m2-surface-high ${
            selected ? 'bg-m2-primary/15' : ''
          }`}
          style={{ paddingLeft: 8 + depth * 12 }}
        >
          <button
            type="button"
            className={`flex min-w-0 flex-1 items-center gap-1 text-left ${
              selected ? 'text-m2-primary' : 'text-m2-on-surface'
            }`}
            onClick={() => {
              setOpen((v) => !v)
              onSelectFolder?.(node)
            }}
          >
            <ChevronRightIcon
              className={`h-4 w-4 shrink-0 text-m2-on-surface-variant transition-transform ${
                open ? 'rotate-90' : ''
              }`}
            />
            <FolderIcon className="h-4 w-4 shrink-0 text-amber-600" />
            <span className="truncate font-medium">{node.name}</span>
          </button>
          {onUploadIntoFolder && (
            <button
              type="button"
              title="Upload vào thư mục này"
              className="shrink-0 rounded p-1 text-m2-on-surface-variant opacity-0 hover:bg-m2-surface-container hover:text-m2-primary group-hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation()
                onUploadIntoFolder(node)
              }}
            >
              <ArrowUpTrayIcon className="h-4 w-4" />
            </button>
          )}
          {onDownloadFolder && (
            <button
              type="button"
              title="Tải thư mục này"
              className="shrink-0 rounded p-1 text-m2-on-surface-variant opacity-0 hover:bg-m2-surface-container hover:text-m2-primary group-hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation()
                onDownloadFolder(node)
              }}
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
            </button>
          )}
        </div>
        {open &&
          node.children.map((child) => (
            <SkillTreeItem
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedId={selectedId}
              onSelectFile={onSelectFile}
              onSelectFolder={onSelectFolder}
              onDownloadFolder={onDownloadFolder}
              onUploadIntoFolder={onUploadIntoFolder}
            />
          ))}
      </div>
    )
  }

  return (
    <button
      type="button"
      className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm ${
        selected
          ? 'bg-m2-primary/15 text-m2-primary'
          : 'text-m2-on-surface hover:bg-m2-surface-high'
      }`}
      style={{ paddingLeft: 8 + depth * 12 + 20 }}
      onClick={() => onSelectFile(node)}
    >
      <DocumentIcon className="h-4 w-4 shrink-0" />
      <span className="truncate">{node.name}</span>
      {node.fileType && (
        <span className="ml-auto shrink-0 text-[10px] uppercase text-m2-on-surface-variant">
          {node.fileType}
        </span>
      )}
    </button>
  )
}

const SkillTree = ({
  nodes,
  selectedId,
  onSelectFile,
  onSelectFolder,
  onDownloadFolder,
  onUploadIntoFolder,
}: SkillTreeProps) => {
  if (!nodes.length) {
    return (
      <p className="px-3 py-6 text-center text-sm text-m2-on-surface-variant">
        Chưa có file trong bộ skill này
      </p>
    )
  }

  return (
    <div className="py-1">
      {nodes.map((node) => (
        <SkillTreeItem
          key={node.id}
          node={node}
          depth={0}
          selectedId={selectedId}
          onSelectFile={onSelectFile}
          onSelectFolder={onSelectFolder}
          onDownloadFolder={onDownloadFolder}
          onUploadIntoFolder={onUploadIntoFolder}
        />
      ))}
    </div>
  )
}

export default SkillTree
